import Sequence from "frontend/communications/dto/sequence"
import {
  _delete, _get, _patch, _post,
  getUrlQueryParameterFromObject,
} from 'frontcore/utils/communications/apiClient'
import { AbortablePromise } from "frontcore/utils/promises/abortable-promise"
import { FORMAT_EXPANDED, STANDARD_DATA_FORMATS } from 'frontend/constants/standards'
import { HTTP_RESET_CONTENT } from 'frontcore/constants/communications'
import { StandardAbilityCertification } from 'frontend/communications/dto/StandardAbilityCertification'
import { URL_BASE_STANDARDS } from "./apiClient"

const URL_BASE_ADDITIONAL_INFO = 'additional-info'
const URL_BASE_SEQUENCES_IN_STANDARD = 'sequences_in_standard'
const URL_BASE_SEQUENCES = 'sequences'
const URL_BASE_SEQUENCE_KEY_CHARACTERISTICS = 'sequence-key-characteristics'
const URL_BASE_REFERENCE_STANDARDS_IN_PERIMETER = 'reference-standards-in-perimeters'
const URL_BASE_ANNOTATIONS = 'annotations'
const URL_BASE_STANDARD_REVIEW = 'standard-reviews'
const URL_BASE_CALENDAR_FILTERS_STANDARD = 'standard-review-calendar-filters'

export const URL_STANDARD_ERGONOMICS_LEVELS = 'standards-ergonomics-levels'
export const URL_STANDARD_PRODUCTION_CONFIGURATIONS = 'standard-production-configurations'
export const URL_STANDARD_SEQUENCE_RSRS_CHARACTERISTICS = 'sequence-rsrs-characteristics'
export const URL_BASE_STANDARD_ABILITY_CERTIFICATIONS = 'standard-ability-certifications'
export const URL_STANDARD_ABILITY_CERTIFICATIONS_IN_PERSON = 'standard-ability-certifications-in-person'

export function createAdditionalInfo (standardId, { description = '', category } = {}) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/${URL_BASE_ADDITIONAL_INFO}/`
  return _post(url, { description, category })
}

export function cloneAdditionalInfo (standardId, additionalInfo = {}) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/${URL_BASE_ADDITIONAL_INFO}/`
  return _post(url, {
    ...additionalInfo,
    id: undefined,
  })
}

export function moveStandard (standardId, payload) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/move/`
  return _patch(url, payload)
}

export function createStandard (data) {
  const url = `${URL_BASE_STANDARDS}/`
  return _post(url, data)
}

export function updateStandard (standardId, data) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/`
  return _patch(url, data)
}

export function deleteStandard (standardId) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/`
  return _delete(url)
}

export function copyStandard (standardId, data) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/copy/`
  return _post(url, data)
}

export function updateAdditionalInfo (id, data) {
  const url = `${URL_BASE_ADDITIONAL_INFO}/${id}/`
  return _patch(url, data)
}

export function removeAdditionalInfo (standardId, id) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/${URL_BASE_ADDITIONAL_INFO}/${id}/`
  return _delete(url)
}

export function updateSequenceInStandard (sequenceInStandardId, data) {
  const url = `${URL_BASE_SEQUENCES_IN_STANDARD}/${sequenceInStandardId}/`
  return _patch(url, data)
}

export function deleteSequenceFromStandard (sequenceInStandardId) {
  const url = `${URL_BASE_SEQUENCES_IN_STANDARD}/${sequenceInStandardId}/`
  return _delete(url)
}

export async function createSequenceInStandard ({ sequence, order }) {
  if (!(sequence instanceof Sequence)) {
    throw new Error('sequence has to be instance of Sequence DTO')
  }

  if (sequence.id) {
    throw new Error("new sequences shouldn't have id")
  }

  const url = `${URL_BASE_SEQUENCES}/`
  const sequenceInDb = await _post(url, sequence.toApiPayload())

  const newSequenceInStandardUrl = `${URL_BASE_SEQUENCES_IN_STANDARD}/`
  return _post(newSequenceInStandardUrl, {
    sequence: sequenceInDb.id,
    standard: sequence.standards[0],
    order,
  })
}

export function removeSequenceInStandard (sequenceInStandardId) {
  const sequenceInStandardUrl = `${URL_BASE_SEQUENCES_IN_STANDARD}/${sequenceInStandardId}`
  return _delete(sequenceInStandardUrl)
}

export function searchSequenceToCopy (searchText) {
  const queryParams = getUrlQueryParameterFromObject({
    active: true,
    format: 'picker',
    search: encodeURIComponent(searchText),
  })

  const url = `${URL_BASE_SEQUENCES}/?${queryParams}`
  return _get(url)
}

export function copySequenceTogether (sequenceId, standardId) {
  const url = `${URL_BASE_SEQUENCES}/${sequenceId}/copy/together/`
  return _patch(url, { standard: standardId }).then(rawDataSequence => new Sequence(rawDataSequence))
}

export function copySequenceIndependently (sequenceId, standardId) {
  const url = `${URL_BASE_SEQUENCES}/${sequenceId}/copy/independently/`
  return _post(url, { standard: standardId }).then(rawDataSequence => new Sequence(rawDataSequence))
}

export function getSequenceLatestStandards (sequenceId) {
  const url = `${URL_BASE_SEQUENCES}/${sequenceId}/latest_standards/`
  return _get(url)
}

export function getAscendantsOfStandard (standardId, format = 'expanded') {
  const url = `${URL_BASE_STANDARDS}/${standardId}/ascendants/?format=${format}`
  return _get(url)
}

export function listStandardErgonomicsLevels () {
  const url = `${URL_STANDARD_ERGONOMICS_LEVELS}/`
  return _get(url)
}

export function getStandardErgonomicsLevelById (id) {
  const url = `${URL_STANDARD_ERGONOMICS_LEVELS}/${id}/`
  return _get(url)
}

export function createStandardErgonomicsLevel (data) {
  const url = URL_STANDARD_ERGONOMICS_LEVELS + "/"
  return _post(url, data).catch(nameDuplicatedErrorHandler)
}

export function updateStandardErgonomicsLevel (id, data) {
  const url = `${URL_STANDARD_ERGONOMICS_LEVELS}/${id}/`
  return _patch(url, data).catch(nameDuplicatedErrorHandler)
}

export function deleteStandardErgonomicsLevel (id) {
  const url = `${URL_STANDARD_ERGONOMICS_LEVELS}/${id}/`
  return _delete(url)
}

export function listStandardProductionConfigurations () {
  const url = `${URL_STANDARD_PRODUCTION_CONFIGURATIONS}/`
  return _get(url)
}

export function createStandardProductionConfiguration (data) {
  const url = `${URL_STANDARD_PRODUCTION_CONFIGURATIONS}/`
  return _post(url, data).catch(nameDuplicatedErrorHandler)
}

export function getStandardProductionConfiguration (id) {
  const url = `${URL_STANDARD_PRODUCTION_CONFIGURATIONS}/${id}`
  return _get(url)
}

export function updateStandardProductionConfiguration (id, data) {
  const url = `${URL_STANDARD_PRODUCTION_CONFIGURATIONS}/${id}/`
  return _patch(url, data).catch(nameDuplicatedErrorHandler)
}

export function deleteStandardProductionConfiguration (id) {
  const url = `${URL_STANDARD_PRODUCTION_CONFIGURATIONS}/${id}/`
  return _delete(url)
}

export function createStandardSequenceRSRSCharacteristic (data) {
  const url = `${URL_STANDARD_SEQUENCE_RSRS_CHARACTERISTICS}/`
  return _post(url, data).catch(nameDuplicatedErrorHandler)
}

export function listStandardSequenceRSRSCharacteristics () {
  const url = `${URL_STANDARD_SEQUENCE_RSRS_CHARACTERISTICS}/?format=table`
  return _get(url)
}

export function fetchStandardSequenceRSRSCharacteristic (id) {
  const url = `${URL_STANDARD_SEQUENCE_RSRS_CHARACTERISTICS}/${id}/`
  return _get(url)
}

export function updateStandardSequenceRSRSCharacteristic (id, { name, description, iconId, enabled }) {
  const url = `${URL_STANDARD_SEQUENCE_RSRS_CHARACTERISTICS}/${id}/`
  return _patch(url, { name, description, icon: iconId, enabled })
}

export function deleteStandardSequenceRSRSCharacteristic (id) {
  const url = `${URL_STANDARD_SEQUENCE_RSRS_CHARACTERISTICS}/${id}/`
  return _delete(url)
}

export function fetchIcon (id) {
  return _get(`icons/${id}/`)
}

export function getStandard (standardId, format = '') {
  let url = `${URL_BASE_STANDARDS}/${standardId}/`
  if (format && !STANDARD_DATA_FORMATS.includes(format)) return
  url += format ? `?format=${format}` : ''
  return _get(url)
}

export function getStandardExpandedFormat (standardId) {
  return getStandard(standardId, FORMAT_EXPANDED)
}

export function getReferenceStandardsByPerimeter (perimeterId) {
  const url = `${URL_BASE_REFERENCE_STANDARDS_IN_PERIMETER}/?perimeter=${perimeterId}`
  return _get(url)
}

export function createReferenceStandard ({ standardId, perimeterId, productionConfigurationId }) {
  const url = `${URL_BASE_REFERENCE_STANDARDS_IN_PERIMETER}/`
  return _post(url, { standard: standardId, perimeter: perimeterId, production_configuration: productionConfigurationId })
}

export function updateReferenceStandard (referenceStandardId, { standardId, perimeterId, productionConfigurationId }) {
  const url = `${URL_BASE_REFERENCE_STANDARDS_IN_PERIMETER}/${referenceStandardId}/`
  return _patch(url, { standard: standardId, perimeter: perimeterId, production_configuration: productionConfigurationId })
}

export function deleteReferenceStandard (referenceStandardId) {
  const url = `${URL_BASE_REFERENCE_STANDARDS_IN_PERIMETER}/${referenceStandardId}/`
  return _delete(url)
}

export function createStandardAbilityCertification (standardAbilityCertification) {
  const url = URL_BASE_STANDARD_ABILITY_CERTIFICATIONS + '/'
  return _post(url, standardAbilityCertification.createApiPayload)
    .catch(nameDuplicatedErrorHandler)
}

export function getStandardAbilityCertificationById (id) {
  return _get(`${URL_BASE_STANDARD_ABILITY_CERTIFICATIONS}/${id}/`).then(response => {
    return new StandardAbilityCertification(response)
  })
}

export function updateStandardAbilityCertification (standardAbilityCertification) {
  return _patch(`${URL_BASE_STANDARD_ABILITY_CERTIFICATIONS}/${standardAbilityCertification.id}/`, standardAbilityCertification.updateApiPayload)
    .catch(nameDuplicatedErrorHandler)
}

export function deleteStandardAbilityCertification (id) {
  return _delete(`${URL_BASE_STANDARD_ABILITY_CERTIFICATIONS}/${id}/`)
}

export function listStandardAbilityCertifications () {
  return _get(URL_BASE_STANDARD_ABILITY_CERTIFICATIONS + '/').then(response => {
    return response.map(rawStandardAbilityCertification => new StandardAbilityCertification(rawStandardAbilityCertification))
  })
}

function nameDuplicatedErrorHandler (response) {
  const error = response.body?.non_field_errors?.[0] ?? 'common.errors.unexpected_error'
  throw new Error(error)
}

export function getAbilityCertificationInPerson ({ personId }) {
  const url = `${URL_STANDARD_ABILITY_CERTIFICATIONS_IN_PERSON}/?person=${personId}`
  return _get(url)
}

export function getAbilityCertificationInPersonById (id) {
  const url = `${URL_STANDARD_ABILITY_CERTIFICATIONS_IN_PERSON}/${id}/`
  return _get(url)
}

export function getAbilityCertificationHistoryForPerson ({ personId, certificationId }) {
  const queryParamsString = getUrlQueryParameterFromObject({
    format: 'history',
    person: personId,
    ability_certification: certificationId,
  }, false)

  const url = `${URL_STANDARD_ABILITY_CERTIFICATIONS_IN_PERSON}/?${queryParamsString}`
  return _get(url)
}

export function createAbilityCertificationInPerson ({ person, ability_certification, certification_date, evidences }) {
  const url = `${URL_STANDARD_ABILITY_CERTIFICATIONS_IN_PERSON}/`
  return _post(url, { person, ability_certification, certification_date, evidences })
}

export function updateAbilityCertificationInPerson (abilityCertificationInPersonId, payload) {
  const url = `${URL_STANDARD_ABILITY_CERTIFICATIONS_IN_PERSON}/${abilityCertificationInPersonId}/`
  return _patch(url, payload)
}

export function createAnnotation (sequenceId, payload) {
  const url = `${URL_BASE_SEQUENCES}/${sequenceId}/${URL_BASE_ANNOTATIONS}/`
  const evidencesIds = payload.evidences?.map(e => e?.id ?? e) ?? []
  return _post(url, { ...payload, evidences: evidencesIds, sequence: sequenceId })
}

export function updateAnnotation (sequenceId, annotationId, payload) {
  const url = `${URL_BASE_SEQUENCES}/${sequenceId}/${URL_BASE_ANNOTATIONS}/${annotationId}/`
  if (payload.evidences) {
    const evidencesIds = payload.evidences.map(e => e?.id ?? e)
    return _patch(url, { ...payload, evidences: evidencesIds, sequence: sequenceId })
  }
  return _patch(url, { ...payload, sequence: sequenceId })
}

export function removeAnnotation (sequenceId, annotationId, payload) {
  const url = `${URL_BASE_SEQUENCES}/${sequenceId}/${URL_BASE_ANNOTATIONS}/${annotationId}/`
  return _delete(url, payload)
}

export function bulkCreateKeySequenceChar (keyCharList) {
  const url = `${URL_BASE_SEQUENCE_KEY_CHARACTERISTICS}/`
  return _post(url, keyCharList)
}

export function bulkDeleteKeySequenceChar (keyCharIdList) {
  const url = `${URL_BASE_SEQUENCE_KEY_CHARACTERISTICS}/?id=${keyCharIdList.join()}`
  return _delete(url)
}

export function getStandardReview (id = null) {
  let url = `${URL_BASE_STANDARD_REVIEW}/`
  if (id) {
    url += `${id}/`
  }
  return _get(url)
}

export function getSwrCalendarFilters () {
  const url = `${URL_BASE_CALENDAR_FILTERS_STANDARD}/`
  return _get(url)
}

export function getActiveStandard (id) {
  return _get(`active-standards/${id}/`)
}

export function getLatestStandardVersion (id) {
  return _get(`latest-standard-version/${id}/`)
}

export function standardDelete (standardId) {
  return _delete(`${URL_BASE_STANDARDS}/${standardId}/`)
}

export function getVariantByExternalIds ({ variant, workstation }) {
  return _get(`workstations/${workstation}/variants/${variant}/`)
}

export function updatePaperStandard (standardId, attachmentId) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/`
  return _patch(url, { paper_standard: attachmentId })
}

export function checkNameAvailability (candidateName, perimeterIds) {
  const queryParams = getUrlQueryParameterFromObject({
    candidate_name: encodeURIComponent(candidateName),
    perimeter_ids: perimeterIds,
  }, false)
  return _get(`check-standard-name-availability/?${queryParams}`)
}

export function checkIfStandardCanBeCopied (standardId) {
  return _get(`${URL_BASE_STANDARDS}/${standardId}/standard_can_be_copied/`)
}

/**
 *
 * @param {number} standardId
 * @returns {AbortablePromise}
 */
export function fetchStandardVersionAscendants (standardId) {
  const url = `${URL_BASE_STANDARDS}/${standardId}/ascendants/`
  return new AbortablePromise((resolve, reject) => {
    _get(url)
      .then(response => {
        if (response?.status === HTTP_RESET_CONTENT) {
          window.history.go(0)
        }
        resolve(response)
      }).catch(e => {
        reject(e)
      })
  })
}
